import {
  ascii,
  decodeText,
  sniffImageType,
  view,
  type BlockReader,
  type EmbeddedPicture,
} from './reader';
import type { MediaInfo, MediaChapter } from './index';

/**
 * Chapters, cover art and length from an MP4 container (.m4b, .m4a, .mp4).
 *
 * MP4 is a tree of length-prefixed boxes, so it can be walked by reading box
 * headers alone and skipping the bodies — which is what keeps this cheap even
 * for a 40-hour book whose sample tables run to tens of megabytes. Only the
 * few small boxes that matter are ever read in full.
 *
 * Chapters come in two forms, and audiobook tools write either or both:
 *   - Nero `chpl`, a list of times and titles in moov/udta;
 *   - a QuickTime chapter *track*: a text track, referenced from the audio
 *     track by `tref/chap`, with one sample per chapter. This is what iTunes,
 *     Audible conversions and most Apple-ecosystem tools produce.
 */

const MAX_CHILDREN = 512;
const MAX_TABLE_BYTES = 8 * 1024 * 1024;
const MAX_COVER_BYTES = 16 * 1024 * 1024;
const MAX_CHAPTERS = 5000;

interface Box {
  type: string;
  start: number;
  contentStart: number;
  end: number;
}

export async function readMp4(r: BlockReader): Promise<MediaInfo> {
  const top = await children(r, 0, r.size);
  const moov = top.find((b) => b.type === 'moov');
  if (!moov) return { chapters: [] };

  const parts = await children(r, moov.contentStart, moov.end);
  const [durationSec, fromChpl, fromTrack, cover] = await Promise.all([
    movieDuration(r, parts.find((b) => b.type === 'mvhd')),
    neroChapters(r, parts.find((b) => b.type === 'udta')),
    chapterTrack(r, parts.filter((b) => b.type === 'trak')),
    coverArt(r, parts.find((b) => b.type === 'udta')),
  ]);

  // When a file carries both, they normally agree; take the fuller list.
  const chapters = fromTrack.length > fromChpl.length ? fromTrack : fromChpl;
  return {
    chapters,
    ...(durationSec ? { durationSec } : {}),
    ...(cover ? { cover } : {}),
  };
}

/* ---------- box walking ---------- */

async function header(r: BlockReader, offset: number, limit: number): Promise<Box | null> {
  if (offset + 8 > limit) return null;
  const h = await r.bytes(offset, 16);
  if (h.length < 8) return null;
  const v = view(h);

  let size = v.getUint32(0);
  let headerLength = 8;
  if (size === 1) {
    if (h.length < 16) return null;
    size = v.getUint32(8) * 2 ** 32 + v.getUint32(12);
    headerLength = 16;
  } else if (size === 0) {
    size = limit - offset; // runs to the end of its parent
  }
  if (size < headerLength || offset + size > limit) return null;

  return { type: ascii(h, 4), start: offset, contentStart: offset + headerLength, end: offset + size };
}

async function children(r: BlockReader, start: number, end: number): Promise<Box[]> {
  const out: Box[] = [];
  let offset = start;
  while (out.length < MAX_CHILDREN) {
    const box = await header(r, offset, end);
    if (!box) break;
    out.push(box);
    offset = box.end;
  }
  return out;
}

async function child(r: BlockReader, parent: Box | undefined, type: string): Promise<Box | undefined> {
  if (!parent) return undefined;
  return (await children(r, parent.contentStart, parent.end)).find((b) => b.type === type);
}

async function body(r: BlockReader, box: Box | undefined, max = MAX_TABLE_BYTES): Promise<Uint8Array | null> {
  if (!box || box.end - box.contentStart > max) return null;
  return r.bytes(box.contentStart, box.end - box.contentStart);
}

/* ---------- mvhd: length of the whole recording ---------- */

async function movieDuration(r: BlockReader, mvhd: Box | undefined): Promise<number | null> {
  const b = await body(r, mvhd, 4096);
  if (!b || b.length < 20) return null;
  const v = view(b);
  const version = v.getUint8(0);
  const timescale = version === 1 ? v.getUint32(20) : v.getUint32(12);
  const duration =
    version === 1 ? v.getUint32(24) * 2 ** 32 + v.getUint32(28) : v.getUint32(16);
  if (!timescale || !duration || duration === 0xffffffff) return null;
  return duration / timescale;
}

/* ---------- Nero chpl ---------- */

/**
 *   uint8 version, uint24 flags, uint32 reserved (version 1 only),
 *   uint8 count, then per chapter: uint64 start (100 ns units),
 *   uint8 title length, UTF-8 title.
 */
async function neroChapters(r: BlockReader, udta: Box | undefined): Promise<MediaChapter[]> {
  const b = await body(r, await child(r, udta, 'chpl'));
  if (!b || b.length < 5) return [];
  const v = view(b);

  let at = 4;
  if (v.getUint8(0) !== 0) at += 4;
  if (at >= b.length) return [];
  const count = v.getUint8(at);
  at += 1;

  const chapters: MediaChapter[] = [];
  for (let i = 0; i < count && at + 9 <= b.length; i++) {
    const hundredNanos = v.getUint32(at) * 2 ** 32 + v.getUint32(at + 4);
    const length = v.getUint8(at + 8);
    at += 9;
    if (at + length > b.length) break;
    const title = decodeText(b.subarray(at, at + length));
    at += length;
    chapters.push({ title: title || `Chapter ${i + 1}`, startSec: hundredNanos / 10_000_000 });
  }
  return chapters.sort((a, b2) => a.startSec - b2.startSec);
}

/* ---------- QuickTime chapter track ---------- */

interface TrackInfo {
  id: number;
  handler: string;
  chapterRefs: number[];
  mdia: Box | undefined;
}

async function trackInfo(r: BlockReader, trak: Box): Promise<TrackInfo> {
  const parts = await children(r, trak.contentStart, trak.end);
  const tkhd = await body(r, parts.find((b) => b.type === 'tkhd'), 4096);
  const mdia = parts.find((b) => b.type === 'mdia');
  const hdlr = await body(r, await child(r, mdia, 'hdlr'), 4096);
  const chap = await body(r, await child(r, parts.find((b) => b.type === 'tref'), 'chap'), 4096);

  let id = 0;
  if (tkhd && tkhd.length >= 24) id = view(tkhd).getUint32(tkhd[0] === 1 ? 20 : 12);

  const chapterRefs: number[] = [];
  if (chap) for (let at = 0; at + 4 <= chap.length; at += 4) chapterRefs.push(view(chap).getUint32(at));

  return { id, handler: hdlr && hdlr.length >= 12 ? ascii(hdlr, 8) : '', chapterRefs, mdia };
}

async function chapterTrack(r: BlockReader, traks: Box[]): Promise<MediaChapter[]> {
  if (traks.length < 2) return [];
  const tracks = await Promise.all(traks.map((t) => trackInfo(r, t)));

  const referenced = new Set(tracks.flatMap((t) => t.chapterRefs));
  const track =
    tracks.find((t) => referenced.has(t.id)) ??
    // No reference: some writers still add a lone text track for chapters.
    (referenced.size === 0 ? tracks.find((t) => t.handler === 'text') : undefined);
  if (!track?.mdia) return [];

  const mdhd = await body(r, await child(r, track.mdia, 'mdhd'), 4096);
  if (!mdhd || mdhd.length < 20) return [];
  const timescale = view(mdhd).getUint32(mdhd[0] === 1 ? 20 : 12);
  if (!timescale) return [];

  const stbl = await child(r, await child(r, track.mdia, 'minf'), 'stbl');
  const tables = await children(r, stbl?.contentStart ?? 0, stbl?.end ?? 0);
  const find = (type: string) => body(r, tables.find((b) => b.type === type));
  const [stts, stsz, stsc, stco, co64] = await Promise.all(
    ['stts', 'stsz', 'stsc', 'stco', 'co64'].map(find),
  );
  if (!stts) return [];

  const starts = sampleStarts(stts, timescale);
  const where =
    stsz && stsc && (stco || co64) ? sampleLocations(stsz, stsc, stco ?? null, co64 ?? null) : [];
  const titles = await sampleTitles(r, where.slice(0, starts.length));

  return starts.map((startSec, i) => ({ title: titles[i] || `Chapter ${i + 1}`, startSec }));
}

/** Start time of each sample, from the time-to-sample table. */
function sampleStarts(stts: Uint8Array, timescale: number): number[] {
  const v = view(stts);
  if (stts.length < 8) return [];
  const entries = v.getUint32(4);
  const starts: number[] = [];
  let time = 0;
  for (let e = 0, at = 8; e < entries && at + 8 <= stts.length; e++, at += 8) {
    const count = v.getUint32(at);
    const delta = v.getUint32(at + 4);
    for (let i = 0; i < count && starts.length < MAX_CHAPTERS; i++) {
      starts.push(time / timescale);
      time += delta;
    }
  }
  return starts;
}

/** File offset and size of each sample: sizes from stsz, grouped into chunks
 *  by stsc, chunks located by stco/co64. */
function sampleLocations(
  stsz: Uint8Array,
  stsc: Uint8Array,
  stco: Uint8Array | null,
  co64: Uint8Array | null,
): { offset: number; size: number }[] {
  const sz = view(stsz);
  if (stsz.length < 12) return [];
  const fixed = sz.getUint32(4);
  const count = Math.min(sz.getUint32(8), MAX_CHAPTERS);
  const sizes = Array.from({ length: count }, (_, i) =>
    fixed || (12 + i * 4 + 4 <= stsz.length ? sz.getUint32(12 + i * 4) : 0),
  );

  const chunkOffsets: number[] = [];
  if (co64) {
    const v = view(co64);
    for (let at = 8; at + 8 <= co64.length; at += 8) {
      chunkOffsets.push(v.getUint32(at) * 2 ** 32 + v.getUint32(at + 4));
    }
  } else if (stco) {
    const v = view(stco);
    for (let at = 8; at + 4 <= stco.length; at += 4) chunkOffsets.push(v.getUint32(at));
  }

  const sc = view(stsc);
  const runs: { firstChunk: number; perChunk: number }[] = [];
  for (let at = 8; at + 12 <= stsc.length; at += 12) {
    runs.push({ firstChunk: sc.getUint32(at), perChunk: sc.getUint32(at + 4) });
  }

  const out: { offset: number; size: number }[] = [];
  let sample = 0;
  for (let chunk = 1; chunk <= chunkOffsets.length && sample < count; chunk++) {
    const run = [...runs].reverse().find((x) => x.firstChunk <= chunk);
    let offset = chunkOffsets[chunk - 1] ?? 0;
    for (let i = 0; i < (run?.perChunk ?? 1) && sample < count; i++, sample++) {
      const size = sizes[sample] ?? 0;
      out.push({ offset, size });
      offset += size;
    }
  }
  return out;
}

/**
 * Each text sample is a uint16 length and the title, UTF-8 or UTF-16 with a
 * byte-order mark. Samples clustered together (the usual case) are read in one
 * go; scattered ones are read individually rather than block by block.
 */
async function sampleTitles(r: BlockReader, samples: { offset: number; size: number }[]): Promise<string[]> {
  if (samples.length === 0) return [];
  const lo = Math.min(...samples.map((s) => s.offset));
  const hi = Math.max(...samples.map((s) => s.offset + s.size));
  const clustered = hi - lo <= 256 * 1024;

  return Promise.all(
    samples.map(async ({ offset, size }) => {
      if (size < 2 || size > 4096) return '';
      try {
        const b = clustered ? await r.bytes(offset, size) : await r.exact(offset, size);
        const length = Math.min(view(b).getUint16(0), b.length - 2);
        const text = b.subarray(2, 2 + length);
        if (text[0] === 0xfe && text[1] === 0xff) return decodeText(text.subarray(2), 'utf-16be');
        if (text[0] === 0xff && text[1] === 0xfe) return decodeText(text.subarray(2), 'utf-16le');
        return decodeText(text);
      } catch {
        return '';
      }
    }),
  );
}

/* ---------- cover: moov/udta/meta/ilst/covr/data ---------- */

async function coverArt(r: BlockReader, udta: Box | undefined): Promise<EmbeddedPicture | null> {
  const meta = await child(r, udta, 'meta');
  if (!meta) return null;

  // ISO `meta` is a full box with 4 bytes of version and flags before its
  // children; QuickTime's is not. Tell them apart by where `hdlr` sits.
  const peek = await r.bytes(meta.contentStart, 8);
  const offset = ascii(peek, 4) === 'hdlr' ? 0 : 4;
  const parts = await children(r, meta.contentStart + offset, meta.end);

  const covr = await child(r, parts.find((b) => b.type === 'ilst'), 'covr');
  const data = await child(r, covr, 'data');
  const b = await body(r, data, MAX_COVER_BYTES);
  if (!b || b.length <= 8) return null;

  const kind = view(b).getUint32(0) & 0xffffff;
  const image = b.subarray(8);
  const mimeType = kind === 14 ? 'image/png' : kind === 13 ? 'image/jpeg' : sniffImageType(image);
  return { mimeType, data: image };
}
